import React, { useRef, useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import {
  Print as PrintIcon,
  Close as CloseIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import { useProductosMaterialContext } from 'context';
import { useAmbienteEcf, ambienteEcfValido } from 'lib/ambienteEcf';
import { FP } from 'components/financepro';
import DocumentoEmpresarialPreview from 'facturapos/DocumentoEmpresarialPreview';
import {
  generarHtmlDocumentoEmpresarial,
  fechaEmisionDgii,
  formatoFechaLargo,
  resolverFechaVencimiento,
} from 'facturapos/documentoEmpresarial';
import { useEmpresaFiscal } from "lib/empresaFiscal";

const toNumber = (value) => parseFloat(String(value ?? '').replace(/,/g, '')) || 0;

/**
 * Vista previa e impresión del e-CF de Gastos Menores (E43),
 * con el mismo diseño empresarial de VistaFactura.
 */
const VistaGastoElectronico = ({
  open,
  onClose,
  gastos = [],
  ncf = '',
  emisionData = {},
  empresa = {},
  rncComprador = '',
}) => {
  const { controller, showError } = useProductosMaterialContext();
  const {
    nombreNegocio,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;

  const nombreEmpresaContexto =
    typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : '';
  const direccionEmpresaContexto =
    typeof direccionEmpresaCtx === 'string' && direccionEmpresaCtx.trim()
      ? direccionEmpresaCtx.trim()
      : '';
  const rncEmpresaContexto =
    typeof rncEmpresaCtx === 'string' && rncEmpresaCtx.trim() ? rncEmpresaCtx.trim() : '';

  // Multi-empresa: el emisor es la empresa fiscal elegida (llega en `empresa`).
  const { activo: multiempresaFiscal } = useEmpresaFiscal();
  const nombreEmpresa =
    (multiempresaFiscal && empresa.nombre) || nombreEmpresaContexto || empresa.nombre || 'Empresa';
  const direccionEmpresa =
    (multiempresaFiscal && empresa.direccion) || direccionEmpresaContexto || empresa.direccion || '';
  const rncEmpresa = (multiempresaFiscal && empresa.rnc) || rncEmpresaContexto || empresa.rnc || '';

  const printRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState('');

  const codigoSeguridad = String(emisionData?.codigo_seguridad || '').trim();
  const fechaFirma = String(emisionData?.fecha_firma || '').trim();
  const montoTotal = useMemo(
    () => gastos.reduce((s, g) => s + toNumber(g.valor), 0),
    [gastos]
  );

  const rncCompradorDigits = String(rncComprador || '').replace(/\D/g, '');
  const rncCompradorValido =
    rncCompradorDigits &&
    rncCompradorDigits !== '999999' &&
    (rncCompradorDigits.length === 9 || rncCompradorDigits.length === 11)
      ? rncCompradorDigits
      : '';

  const fechaGasto = gastos[0]?.fecha || '';
  const fechaDocumento = formatoFechaLargo(fechaGasto) || formatoFechaLargo(new Date());

  const fechaVencimiento = useMemo(
    () => resolverFechaVencimiento(emisionData),
    [emisionData]
  );

  // Ambiente de la emisión si viene; si no, el del tenant (varinicial.ambienteecf).
  const ambienteTenant = useAmbienteEcf();
  const ambienteQr = ambienteEcfValido(emisionData?.ambiente) || ambienteTenant;

  const qrUrl = useMemo(() => {
    if (!open || !ncf || !ambienteQr) return '';
    const rncEmisor = (rncEmpresa || '').replace(/-/g, '');
    const fechaEmisionDgiiFmt = fechaEmisionDgii(fechaFirma || fechaGasto);
    return (
      `https://ecf.dgii.gov.do/${ambienteQr}/ConsultaTimbre?` +
      `RncEmisor=${encodeURIComponent(rncEmisor)}` +
      (rncCompradorValido ? `&RncComprador=${encodeURIComponent(rncCompradorValido)}` : '') +
      `&ENCF=${encodeURIComponent(String(ncf).toUpperCase())}` +
      `&FechaEmision=${encodeURIComponent(fechaEmisionDgiiFmt)}` +
      `&MontoTotal=${encodeURIComponent(montoTotal.toFixed(2))}` +
      `&FechaFirma=${encodeURIComponent(fechaFirma)}` +
      `&CodigoSeguridad=${encodeURIComponent(codigoSeguridad)}`
    );
  }, [
    open,
    ncf,
    rncEmpresa,
    rncCompradorValido,
    fechaFirma,
    fechaGasto,
    montoTotal,
    codigoSeguridad,
    ambienteQr,
  ]);

  useEffect(() => {
    if (!open || !qrUrl) {
      setQrDataUrl('');
      return undefined;
    }
    let cancelado = false;
    QRCode.toDataURL(qrUrl, { margin: 1, width: 220 })
      .then((url) => {
        if (!cancelado) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelado) setQrDataUrl('');
      });
    return () => {
      cancelado = true;
    };
  }, [open, qrUrl]);

  const items = useMemo(
    () =>
      gastos.map((g) => ({
        codigo: String(g.id || ''),
        descripcion: (g.descripcion || g.tipogastos || 'Gasto').trim(),
        cantidad: 1,
        precio: toNumber(g.valor),
        descuento: 0,
        subtotal: toNumber(g.valor),
      })),
    [gastos]
  );

  const filasTotales = useMemo(
    () => [
      { label: 'Subtotal', value: montoTotal },
      { label: 'Cantidad de gastos', value: String(gastos.length) },
    ],
    [montoTotal, gastos.length]
  );

  const parteComprador = rncCompradorValido
    ? {
        titulo: 'Comprador',
        nombre: `RNC ${rncCompradorValido}`,
        lineas: [],
      }
    : null;

  const metaExtra = useMemo(() => {
    const extra = [];
    if (ncf) extra.push({ label: 'NCF', value: ncf });
    //if (emisionData?.estado) extra.push({ label: 'Estado DGII', value: emisionData.estado });
    return extra;
  }, [ncf, fechaVencimiento, emisionData?.estado]);

  const handleImprimir = () => {
    const ventana = window.open('', '_blank');
    if (!ventana) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    const htmlContent = generarHtmlDocumentoEmpresarial({
      tituloDocumento: 'GASTOS MENORES ELECTRÓNICO',
      numero: ncf,
      documento: '',
      fecha: fechaDocumento,
      metaExtra,
      fechaVencimiento,
      parte: parteComprador,
      items,
      filasTotales,
      totalLabel: 'TOTAL',
      totalValue: montoTotal,
      qrDataUrl,
      codigoSeguridad,
      fechaFirma,
      nombreEmpresa,
      direccionEmpresa,
      rncEmpresa,
      mostrarInfoEmpresa: true,
      mensajeGracias: '',
      leyendaPiePagina: 'Documento generado electrónicamente',
    });
    ventana.document.write(htmlContent);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => {
      ventana.print();
      ventana.close();
    }, 250);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          maxHeight: '92vh',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.6,
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.2,
            bgcolor: FP.color.navySoft,
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography
            sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.98rem', lineHeight: 1.2 }}
          >
            Gasto electrónico {ncf || '—'}
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem' }}>
            Vista previa · Formato empresarial
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button
          startIcon={<CloseIcon />}
          onClick={onClose}
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: FP.color.textSecondary,
            borderRadius: 1.4,
            '&:hover': { bgcolor: FP.color.surfaceAlt },
          }}
        >
          Cerrar
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: FP.color.surface }}>
        <DocumentoEmpresarialPreview
          printRef={printRef}
          mostrarInfoEmpresa
          nombreEmpresa={nombreEmpresa}
          direccionEmpresa={direccionEmpresa}
          rncEmpresa={rncEmpresa}
          tituloDocumento="GASTOS MENORES ELECTRÓNICO (E43)"
          numero={ncf}
          documento=""
          fecha={fechaDocumento}
          fechaVencimiento={fechaVencimiento}
          metaExtra={metaExtra}
          parte={parteComprador}
          items={items}
          filasTotales={filasTotales}
          totalLabel="TOTAL"
          totalValue={montoTotal}
          observaciones=""
          qrDataUrl={qrDataUrl}
          codigoSeguridad={codigoSeguridad}
          fechaFirma={fechaFirma}
          mensajeGracias=""
          footerNote="Documento generado electrónicamente"
          varianteTabla="listado"
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderTop: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          gap: 1,
        }}
      >
        <Button
          startIcon={<PrintIcon />}
          onClick={handleImprimir}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 1.4,
            borderColor: FP.color.border,
            color: FP.color.textPrimary,
            '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
          }}
        >
          Imprimir
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            borderRadius: 1.4,
            fontWeight: 700,
            bgcolor: FP.color.navy,
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VistaGastoElectronico;
